import * as Tone from 'tone';

export class AudioEngine {
  private player: Tone.Player | null = null;
  private pitchShift: Tone.PitchShift | null = null;
  private currentLoop: { start: number; end: number } | null = null;
  private onLoopComplete: (() => void) | null = null;
  private loopMonitorInterval: NodeJS.Timeout | null = null;
  private lastLoopTime: number = 0;
  private userPitchShift: number = 0;
  private currentPlaybackRate: number = 1;
  /** Object URL owned by the engine; revoked on next load or dispose. */
  private ownedObjectUrl: string | null = null;

  /**
   * Load a track. If `objectUrl` is provided, the engine takes ownership of it
   * and will revoke it when the next track loads or when the engine is disposed.
   * This prevents premature revocation while Tone.js may still reference the URL.
   */
  async loadTrack(url: string, objectUrl?: string): Promise<void> {
    await Tone.start(); // Required for audio context

    if (this.player) {
      this.player.dispose();
    }
    if (this.pitchShift) {
      this.pitchShift.dispose();
    }
    if (this.ownedObjectUrl) {
      URL.revokeObjectURL(this.ownedObjectUrl);
      this.ownedObjectUrl = null;
    }

    this.pitchShift = new Tone.PitchShift().toDestination();
    this.player = new Tone.Player();

    // Connect player through pitch shift to destination
    this.player.connect(this.pitchShift);

    // Load the audio file and wait for it to complete
    await this.player.load(url);

    if (objectUrl) {
      this.ownedObjectUrl = objectUrl;
    }
  }

  setLoop(startTime: number, endTime: number, onComplete?: () => void): void {
    if (!this.player) return;

    this.currentLoop = { start: startTime, end: endTime };
    this.onLoopComplete = onComplete || null;
    
    this.player.loop = true;
    this.player.loopStart = startTime;
    this.player.loopEnd = endTime;
    
    // Set up loop monitoring if callback is provided
    if (onComplete) {
      this.setupLoopMonitoring();
    }
  }

  private setupLoopMonitoring(): void {
    // Clear any existing monitor
    if (this.loopMonitorInterval) {
      clearInterval(this.loopMonitorInterval);
    }

    this.lastLoopTime = Date.now();

    // Monitor playback position to detect loops
    this.loopMonitorInterval = setInterval(() => {
      if (!this.player || !this.currentLoop || this.player.state !== 'started') {
        return;
      }

      // Check if enough time has passed for a loop to have completed
      const now = Date.now();
      const loopDuration = (this.currentLoop.end - this.currentLoop.start) * 1000 / this.player.playbackRate;
      
      if (now - this.lastLoopTime >= loopDuration * 0.95) { // 95% threshold to account for timing variance
        this.lastLoopTime = now;
        if (this.onLoopComplete) {
          this.onLoopComplete();
        }
      }
    }, 100); // Check every 100ms
  }

  setPlaybackRate(bpm: number, originalBpm: number): void {
    if (!this.player || !this.pitchShift) return;
    const rate = bpm / originalBpm;
    this.player.playbackRate = rate;
    this.currentPlaybackRate = rate;
    
    // When playback rate changes, pitch changes too. Compensate for it.
    // Formula: semitones = 12 * log2(rate)
    // We want to shift pitch in opposite direction to maintain original pitch
    const pitchCompensation = -12 * Math.log2(rate);
    
    // Apply both compensation and user-requested pitch shift
    this.pitchShift.pitch = pitchCompensation + this.userPitchShift;
  }

  setPitchShift(semitones: number): void {
    if (!this.pitchShift) return;
    this.userPitchShift = semitones;
    
    // Recalculate total pitch shift including playback rate compensation
    const pitchCompensation = -12 * Math.log2(this.currentPlaybackRate);
    this.pitchShift.pitch = pitchCompensation + this.userPitchShift;
  }

  play(startTime?: number): void {
    if (!this.player) return;
    
    // Check if buffer is loaded
    if (!this.player.loaded) {
      console.error('Cannot play: buffer not loaded');
      return;
    }
    
    if (startTime !== undefined) {
      this.player.start(undefined, startTime);
    } else if (this.currentLoop) {
      this.player.start(undefined, this.currentLoop.start);
    } else {
      this.player.start();
    }
  }

  stop(): void {
    if (!this.player) return;
    this.player.stop();
    
    // Clear loop monitoring
    if (this.loopMonitorInterval) {
      clearInterval(this.loopMonitorInterval);
      this.loopMonitorInterval = null;
    }
  }

  isPlaying(): boolean {
    return this.player?.state === 'started';
  }

  /** Duration of the currently-loaded buffer, in seconds (0 if nothing loaded). */
  getDuration(): number {
    if (!this.player || !this.player.loaded) return 0;
    const d = this.player.buffer?.duration;
    return typeof d === 'number' && isFinite(d) ? d : 0;
  }

  dispose(): void {
    // Clear loop monitoring
    if (this.loopMonitorInterval) {
      clearInterval(this.loopMonitorInterval);
      this.loopMonitorInterval = null;
    }
    
    if (this.player) {
      this.player.dispose();
      this.player = null;
    }
    if (this.pitchShift) {
      this.pitchShift.dispose();
      this.pitchShift = null;
    }
    if (this.ownedObjectUrl) {
      URL.revokeObjectURL(this.ownedObjectUrl);
      this.ownedObjectUrl = null;
    }
  }

  getCurrentTime(): number {
    return Tone.Transport.seconds;
  }
}

export class MetronomeEngine {
  // A short filtered-noise click sounds much more like a real metronome than
  // a membrane synth. We use two filter frequencies so the accent beats of
  // each bar get a brighter "tock" while the rest are a softer "tick".
  private noise: Tone.NoiseSynth | null = null;
  private filter: Tone.Filter | null = null;
  private gain: Tone.Gain | null = null;
  private loop: Tone.Loop | null = null;
  private beatCount: number = 0;
  private beatsPerBar: number = 4;
  private accents: Set<number> = new Set([1]); // 1-indexed beats inside the bar
  private beatsBeforeIncrease: number = 4;
  private onBeatCallback: ((beat: number) => void) | null = null;
  private onIncreaseCallback: (() => void) | null = null;

  private static readonly ACCENT_FREQ = 2500; // Hz – downbeat "tock"
  private static readonly CLICK_FREQ = 1200;  // Hz – regular "tick"

  constructor() {
    this.filter = new Tone.Filter({
      type: 'bandpass',
      frequency: MetronomeEngine.CLICK_FREQ,
      Q: 2.5,
    });

    this.gain = new Tone.Gain(2).toDestination();

    this.filter.connect(this.gain);

    this.noise = new Tone.NoiseSynth({
      noise: { type: 'white' },
      envelope: { attack: 0.001, decay: 0.04, sustain: 0, release: 0.02 },
      volume: 6,
    }).connect(this.filter);
  }

  /**
   * Start the metronome.
   *
   * @param bpm                  Beats per minute, where one beat is one note of
   *                             the time-signature's beat unit (e.g. an 8th note
   *                             in 6/8). This matches how musicians read tempo
   *                             marks like "♩=120" or "♪=120".
   * @param beatsBeforeIncrease  Decoupled from the time signature: how many
   *                             beats to count before firing onIncrease.
   * @param timeSig              Time signature controlling bar length & accents.
   */
  start(
    bpm: number,
    beatsBeforeIncrease: number,
    onBeat?: (beat: number) => void,
    onIncrease?: () => void,
    timeSig: { beatsPerBar: number; beatUnit: number; accents?: number[] } = {
      beatsPerBar: 4, beatUnit: 4, accents: [1],
    },
  ): void {
    Tone.start();

    this.beatsBeforeIncrease = Math.max(1, beatsBeforeIncrease);
    this.beatsPerBar = Math.max(1, timeSig.beatsPerBar);
    this.accents = new Set(timeSig.accents && timeSig.accents.length > 0 ? timeSig.accents : [1]);
    this.beatCount = 0;
    this.onBeatCallback = onBeat || null;
    this.onIncreaseCallback = onIncrease || null;

    // Tone.Transport.bpm always represents quarter-notes-per-minute. To make
    // the user's `bpm` mean "beats of the chosen unit per minute", convert:
    //   transportBpm = bpm * 4 / beatUnit
    // e.g. 8th-note pulse at 120bpm  → transport bpm = 60 (so '8n' fires 120/min)
    //      half-note pulse at 60bpm  → transport bpm = 120 (so '2n' fires 60/min)
    const beatUnit = timeSig.beatUnit > 0 ? timeSig.beatUnit : 4;
    Tone.Transport.bpm.value = (bpm * 4) / beatUnit;

    if (this.loop) {
      this.loop.dispose();
    }

    const interval = `${beatUnit}n`;

    this.loop = new Tone.Loop((time) => {
      if (!this.noise || !this.filter) return;

      const beatInBar = (this.beatCount % this.beatsPerBar) + 1; // 1-indexed
      const isAccent = this.accents.has(beatInBar);

      this.filter.frequency.setValueAtTime(
        isAccent ? MetronomeEngine.ACCENT_FREQ : MetronomeEngine.CLICK_FREQ,
        time,
      );
      this.noise.triggerAttackRelease('32n', time, isAccent ? 1 : 0.7);

      this.beatCount++;

      if (this.onBeatCallback) {
        this.onBeatCallback(this.beatCount);
      }

      if (this.beatCount % this.beatsBeforeIncrease === 0 && this.onIncreaseCallback) {
        this.onIncreaseCallback();
      }
    }, interval);

    this.loop.start(0);
    Tone.Transport.start();
  }

  /**
   * Update tempo while playing. The supplied `bpm` is in the same units as
   * passed to `start()`; we keep the original beat-unit scaling.
   */
  setBpm(bpm: number, beatUnit: number = 4): void {
    Tone.Transport.bpm.value = (bpm * 4) / (beatUnit > 0 ? beatUnit : 4);
  }

  stop(): void {
    if (this.loop) {
      this.loop.stop();
      Tone.Transport.stop();
    }
    this.beatCount = 0;
  }

  dispose(): void {
    if (this.loop) {
      this.loop.dispose();
      this.loop = null;
    }
    if (this.noise) {
      this.noise.dispose();
      this.noise = null;
    }
    if (this.filter) {
      this.filter.dispose();
      this.filter = null;
    }
    if (this.gain) {
      this.gain.dispose();
      this.gain = null;
    }
    Tone.Transport.stop();
  }
}
